/**
 * The local agent store (M4 spec §10.2, plan I4): a structure-of-arrays record per strategic agent in the player's
 * system, filled from the `LocalManifest` and `LocalDelta`s. Every agent is a **contact** (a kinematic position
 * computed from its step and the Reach clock, a HUD marker, no mesh) until the tier step (`realShips.ts`) promotes
 * it to a real ECS ship (`eid >= 0`), which then flies its own position.
 *
 * Kinematics along a leg: `from + (to − from) × smoothstep(u)`, `u` the step's time fraction, so the cruise covers
 * most of the distance mid-leg. Docked agents are inside their station (hidden); mining and holding agents stay at
 * the node they flew to (holding at a station: on its hold orbit, SC-5d3). `progress` is 0..1 along the current step (the sync reports it).
 *
 * RR-F1 BL-1a (D-803 C4, D-828 BL-T (a)): a **stand-by candidate** (a miner, trader or supplier holding at a
 * station or base node, not the player's own) is capped per station by `refreshStandby` (message rate): the first
 * `n` by rank (miners, then traders, then suppliers; slot order within a role) show (`standby` 1), the rest wait
 * inside like the docked (`standby` 2: `isDockedLike`). With `standby.json standbyVisiblePerStation` 0 the worker
 * sends no stand-by step and the subsystem never ranks, so every `standby` stays 0 (today's behaviour).
 *
 * Adding, removing and applying steps are message-rate work (they may allocate: ids and strings); `stepContacts`
 * and the readers run per tick and allocate nothing (D-014). Positions are float64 world metres (D-011).
 */
import type { FactionId, LocalAgent, LocalFleet, LocalJob, LocalStep, RoleId } from '../strategic/types'
import { LEG_PURPOSE } from '../strategic/types'
import { hashString } from '../space/hash'
import { Agent } from './components'
import { wareName } from '../ui/strategic/names'

/** `ContactStore.kind` codes, one per `LocalStep.kind`. */
export const STEP_KIND = { docked: 0, leg: 1, mining: 2, holding: 3 } as const
export type StepKindCode = (typeof STEP_KIND)[keyof typeof STEP_KIND]

/** Leg purposes by code (`ContactStore.purpose`; `LEG_PURPOSE` of the strategic core). */
export const PURPOSE = LEG_PURPOSE

export interface ContactStore {
  readonly capacity: number
  count: number
  /** Slot → agent id, role, faction, hull, ship def (strings: message-rate data). */
  readonly ids: string[]
  readonly role: RoleId[]
  readonly faction: FactionId[]
  readonly hull: string[]
  readonly shipDef: string[]
  /** Slot → the id the step aims at: the station (docked, leg to a station), field (mining) or node. */
  readonly targetId: string[]
  readonly slotById: Map<string, number>
  /** M6 (plan P22): slot → the player fleet block (`LocalAgent.fleet`), null for every other agent (message rate). */
  readonly fleet: (LocalFleet | null)[]
  /** M6 (plan P22): 1 when the slot is one of the player corporation's own ships (the tier step promotes them first). */
  readonly own: Uint8Array
  readonly team: Int32Array
  /** 0..1 of the hull (the worker's scale). */
  readonly hp: Float32Array
  readonly cargoQty: Float32Array
  readonly kind: Uint8Array
  /** `PURPOSE` code of a leg (0 otherwise). */
  readonly purpose: Uint8Array
  /** `NODE_KIND` code of the step's target node (legs and holding; 0 otherwise). */
  readonly targetKind: Uint8Array
  readonly t0: Float64Array
  readonly t1: Float64Array
  readonly fx: Float64Array
  readonly fy: Float64Array
  readonly fz: Float64Array
  readonly tx: Float64Array
  readonly ty: Float64Array
  readonly tz: Float64Array
  /** Current float64 world position (kinematic for contacts; the ship's `Pos64` for real ships, synced by the tier step). */
  readonly x: Float64Array
  readonly y: Float64Array
  readonly z: Float64Array
  readonly progress: Float32Array
  /** The real ship's entity, or −1 for a contact. */
  readonly eid: Int32Array
  /** 1 once the current step's end was reported (never twice per step). */
  readonly reported: Uint8Array
  /** Sim time of the last combat contact (hit or hostile near) of the real ship; demotion waits `demoteCalmS`. */
  readonly combatAt: Float64Array
  /** BL-1a: 0 not a stand-by candidate (or not ranked), 1 standing by and shown, 2 standing by and hidden (docked-like). */
  readonly standby: Uint8Array
  /**
   * BL-2b: slot → the purpose label ("Ferrite → Halvard Refinery"; '' = none, today's text), built by `applyJob` once per
   * step change (message rate); the HUD reads it per frame without building strings.
   */
  readonly label: string[]
  /** BL-2b: station id → display name, from the manifest's stations (`setStationNames`; unknown ids show the id). */
  readonly stationNames: Map<string, string>
  /** [0]: the Reach time the store was last stepped to (a typed slot: a float64 argument is boxed per call, D-014). */
  readonly time: Float64Array
}

export function createContactStore(capacity: number): ContactStore {
  const f64 = (): Float64Array => new Float64Array(capacity)
  return {
    capacity,
    count: 0,
    ids: new Array<string>(capacity).fill(''),
    role: new Array<RoleId>(capacity).fill('trader'),
    faction: new Array<FactionId>(capacity).fill('mutual'),
    hull: new Array<string>(capacity).fill(''),
    shipDef: new Array<string>(capacity).fill(''),
    targetId: new Array<string>(capacity).fill(''),
    slotById: new Map(),
    fleet: new Array<LocalFleet | null>(capacity).fill(null),
    own: new Uint8Array(capacity),
    team: new Int32Array(capacity),
    hp: new Float32Array(capacity),
    cargoQty: new Float32Array(capacity),
    kind: new Uint8Array(capacity),
    purpose: new Uint8Array(capacity),
    targetKind: new Uint8Array(capacity),
    t0: f64(),
    t1: f64(),
    fx: f64(),
    fy: f64(),
    fz: f64(),
    tx: f64(),
    ty: f64(),
    tz: f64(),
    x: f64(),
    y: f64(),
    z: f64(),
    progress: new Float32Array(capacity),
    eid: new Int32Array(capacity).fill(-1),
    reported: new Uint8Array(capacity),
    combatAt: new Float64Array(capacity).fill(-1e9),
    standby: new Uint8Array(capacity),
    label: new Array<string>(capacity).fill(''),
    stationNames: new Map(),
    time: new Float64Array(1),
  }
}

/** Resolves a node id (a station or field) to its float64 world position; false when unknown. */
export type NodeResolver = (id: string, out: { x: number; y: number; z: number }) => boolean

/**
 * SC-5d1: resolves a station id to its bay mouth and its bay entry (mouth + out × the autodock `entryAlong`, outside
 * the hull), float64 world; false when the id is no station or its bay is unknown (the leg then keeps the centre).
 * SC-5d3: `hull`, when given, receives the station's bounding radius (the hold orbit's R).
 */
export type BayEndResolver = (id: string, mouth: { x: number; y: number; z: number }, entry: { x: number; y: number; z: number }, hull?: { radius: number }) => boolean

const resolved = { x: 0, y: 0, z: 0 }
const bayEntry = { x: 0, y: 0, z: 0 }
const holdHull = { radius: 0 }

/**
 * Adds `agent` (or refreshes it when its id is known) and applies its step at Reach time `now`. Returns the slot,
 * or −1 when the store is full (logged by the caller).
 */
export function upsertAgent(s: ContactStore, agent: Readonly<LocalAgent>, now: number, resolve: NodeResolver, bayEnds: BayEndResolver | null = null, holdOrbitM = 0): number {
  let slot = s.slotById.get(agent.id) ?? -1
  if (slot < 0) {
    if (s.count >= s.capacity) return -1
    slot = s.count++
    s.slotById.set(agent.id, slot)
    s.eid[slot] = -1
    s.combatAt[slot] = -1e9
    s.ids[slot] = agent.id
  }
  s.role[slot] = agent.role
  s.faction[slot] = agent.faction
  s.hull[slot] = agent.hull
  s.shipDef[slot] = agent.shipDef
  s.team[slot] = agent.teamId
  s.fleet[slot] = agent.fleet ?? null
  s.own[slot] = agent.fleet !== undefined ? 1 : 0
  s.hp[slot] = agent.hp
  s.cargoQty[slot] = agent.cargo === null ? 0 : agent.cargo.qty
  applyStep(s, slot, agent.step, now, resolve, bayEnds, holdOrbitM)
  applyJob(s, slot, agent.job)
  return slot
}

/**
 * Applies a new step to `slot` at Reach time `now` (message rate). A leg starts at its `from` node; docked,
 * mining and holding keep the agent where it is unless the node resolves (a manifest agent seen first there).
 * SC-5d1: with `bayEnds`, a leg leaving a station starts at its bay mouth and a leg to a station ends at its bay
 * entry, so no contact starts, ends or is promoted inside a hull. SC-5d3: a contact holding at a station sits on the
 * hold orbit its pilot flies (`holdSpot`); the target stays the node.
 */
export function applyStep(s: ContactStore, slot: number, step: Readonly<LocalStep>, now: number, resolve: NodeResolver, bayEnds: BayEndResolver | null = null, holdOrbitM = 0): void {
  s.reported[slot] = 0
  s.progress[slot] = 0
  s.standby[slot] = 0
  s.purpose[slot] = 0
  s.targetKind[slot] = 0
  switch (step.kind) {
    case 'leg': {
      s.kind[slot] = STEP_KIND.leg
      s.purpose[slot] = LEG_PURPOSE[step.purpose]
      s.targetKind[slot] = NODE_KIND_CODE[step.to.kind]
      s.targetId[slot] = step.to.id
      s.t0[slot] = step.t0
      s.t1[slot] = step.t1
      s.fx[slot] = step.from.pos[0]
      s.fy[slot] = step.from.pos[1]
      s.fz[slot] = step.from.pos[2]
      s.tx[slot] = step.to.pos[0]
      s.ty[slot] = step.to.pos[1]
      s.tz[slot] = step.to.pos[2]
      if (bayEnds !== null && bayEnds(step.from.id, resolved, bayEntry)) {
        s.fx[slot] = resolved.x
        s.fy[slot] = resolved.y
        s.fz[slot] = resolved.z
      }
      if (bayEnds !== null && bayEnds(step.to.id, resolved, bayEntry)) {
        s.tx[slot] = bayEntry.x
        s.ty[slot] = bayEntry.y
        s.tz[slot] = bayEntry.z
      }
      if (s.eid[slot] < 0) kinematicPosition(s, slot, now)
      return
    }
    case 'docked':
      s.kind[slot] = STEP_KIND.docked
      s.targetId[slot] = step.station
      s.t0[slot] = now
      s.t1[slot] = step.until
      break
    case 'mining':
      s.kind[slot] = STEP_KIND.mining
      s.targetId[slot] = step.field
      s.t0[slot] = now
      s.t1[slot] = step.until
      break
    case 'holding':
      s.kind[slot] = STEP_KIND.holding
      s.targetKind[slot] = NODE_KIND_CODE[step.node.kind]
      s.targetId[slot] = step.node.id
      s.t0[slot] = now
      s.t1[slot] = step.until
      s.tx[slot] = step.node.pos[0]
      s.ty[slot] = step.node.pos[1]
      s.tz[slot] = step.node.pos[2]
      break
  }
  // A step at a node: the node is the target; a contact (not a flying real ship) sits there.
  if (s.kind[slot] !== STEP_KIND.holding && resolve(s.targetId[slot], resolved)) {
    s.tx[slot] = resolved.x
    s.ty[slot] = resolved.y
    s.tz[slot] = resolved.z
  } else if (s.kind[slot] !== STEP_KIND.holding) {
    s.tx[slot] = s.x[slot]
    s.ty[slot] = s.y[slot]
    s.tz[slot] = s.z[slot]
  }
  s.fx[slot] = s.tx[slot]
  s.fy[slot] = s.ty[slot]
  s.fz[slot] = s.tz[slot]
  if (s.eid[slot] < 0) {
    s.x[slot] = s.tx[slot]
    s.y[slot] = s.ty[slot]
    s.z[slot] = s.tz[slot]
    if (s.kind[slot] === STEP_KIND.holding && bayEnds !== null) holdSpot(s, slot, bayEnds, holdOrbitM)
  }
}

/**
 * SC-5d3: moves holding contact `slot` from its node (a station centre, inside the hull) onto the hold orbit the pilot
 * flies (`ai/traffic.ts` hold phase): `R + holdOrbitM` round the node in its horizontal plane, R the station's
 * bounding radius about its origin (so the bay row's height, D-905, does not matter), at an angle from the agent id so
 * contacts holding there don't stack. Not a station (fields, relays) or unknown: stays at the node. Message rate.
 */
function holdSpot(s: ContactStore, slot: number, bayEnds: BayEndResolver, holdOrbitM: number): void {
  if (!bayEnds(s.targetId[slot], resolved, bayEntry, holdHull)) return
  const r = holdHull.radius + holdOrbitM
  const a = (hashString(s.ids[slot]) / 4294967296) * 2 * Math.PI
  s.x[slot] = s.tx[slot] + Math.cos(a) * r
  s.z[slot] = s.tz[slot] + Math.sin(a) * r
}

/** `NodeRef.kind` → code (the strategic `NODE_KIND` order). */
const NODE_KIND_CODE = { station: 0, field: 1, relay: 2, gate: 3, base: 4 } as const
export const TARGET_KIND = NODE_KIND_CODE

/**
 * Removes `slot` (swap-remove: the last slot moves into it and its real ship's `Agent.index` follows). The caller
 * despawns the slot's real ship first. Message rate.
 */
export function removeAgent(s: ContactStore, slot: number): void {
  const last = s.count - 1
  s.slotById.delete(s.ids[slot])
  if (slot !== last) {
    s.ids[slot] = s.ids[last]
    s.role[slot] = s.role[last]
    s.faction[slot] = s.faction[last]
    s.hull[slot] = s.hull[last]
    s.shipDef[slot] = s.shipDef[last]
    s.targetId[slot] = s.targetId[last]
    s.fleet[slot] = s.fleet[last]
    s.label[slot] = s.label[last]
    const typed = [s.team, s.hp, s.cargoQty, s.kind, s.purpose, s.targetKind, s.t0, s.t1, s.fx, s.fy, s.fz, s.tx, s.ty, s.tz, s.x, s.y, s.z, s.progress, s.eid, s.reported, s.combatAt, s.own, s.standby]
    for (let k = 0; k < typed.length; k++) typed[k][slot] = typed[k][last]
    s.slotById.set(s.ids[slot], slot)
    const eid = s.eid[slot]
    if (eid >= 0) Agent.index[eid] = slot
  }
  s.eid[last] = -1
  s.ids[last] = ''
  s.fleet[last] = null
  s.label[last] = ''
  s.own[last] = 0
  s.standby[last] = 0
  s.count = last
}

/** Removes every agent (the system was left). The caller despawned the real ships. */
export function clearContacts(s: ContactStore): void {
  s.slotById.clear()
  for (let i = 0; i < s.count; i++) {
    s.eid[i] = -1
    s.ids[i] = ''
    s.fleet[i] = null
    s.label[i] = ''
    s.own[i] = 0
    s.standby[i] = 0
  }
  s.count = 0
}

function smoothstep(u: number): number {
  return u * u * (3 - 2 * u)
}

/** The step's time fraction at Reach time `now`, clamped to 0..1 (1 for a step with no duration). */
export function timeFraction(s: ContactStore, slot: number, now: number): number {
  const d = s.t1[slot] - s.t0[slot]
  if (!(d > 0)) return 1
  const u = (now - s.t0[slot]) / d
  return u < 0 ? 0 : u > 1 ? 1 : u
}

/** Writes the kinematic position of `slot` at Reach time `now` into `x/y/z` (and its progress). Allocation-free. */
export function kinematicPosition(s: ContactStore, slot: number, now: number): void {
  const u = timeFraction(s, slot, now)
  s.progress[slot] = u
  if (s.kind[slot] !== STEP_KIND.leg) return
  const k = smoothstep(u)
  s.x[slot] = s.fx[slot] + (s.tx[slot] - s.fx[slot]) * k
  s.y[slot] = s.fy[slot] + (s.ty[slot] - s.fy[slot]) * k
  s.z[slot] = s.fz[slot] + (s.tz[slot] - s.fz[slot]) * k
}

/** Advances every contact (not the real ships) to Reach time `now`. Allocation-free. */
export function stepContacts(s: ContactStore, now: number): void {
  s.time[0] = now
  advanceContacts(s)
}

/** Advances every contact to `s.time[0]` (the per-tick form: no float64 argument to box). Allocation-free. */
export function advanceContacts(s: ContactStore): void {
  const now = s.time[0]
  for (let i = 0; i < s.count; i++) {
    if (s.eid[i] < 0) kinematicPosition(s, i, now)
    else s.progress[i] = timeFraction(s, i, now)
  }
}

/** True when the agent shows in space (a HUD contact or a real ship): everyone but the docked and the hidden stand-by. Allocation-free. */
export function isVisible(s: ContactStore, slot: number): boolean {
  return s.kind[slot] !== STEP_KIND.docked && s.standby[slot] !== 2
}

/** BL-1a: true when the agent is inside its station: docked, or standing by beyond its station's cap. Allocation-free. */
export function isDockedLike(s: ContactStore, slot: number): boolean {
  return s.kind[slot] === STEP_KIND.docked || s.standby[slot] === 2
}

/** BL-1a: a stand-by candidate: a miner, trader or supplier holding at a station or base node, not the player's own. */
export function isStandbyCandidate(s: ContactStore, slot: number): boolean {
  if (s.kind[slot] !== STEP_KIND.holding || s.own[slot] !== 0) return false
  const tk = s.targetKind[slot]
  if (tk !== TARGET_KIND.station && tk !== TARGET_KIND.base) return false
  const r = s.role[slot]
  return r === 'miner' || r === 'trader' || r === 'supplier'
}

/** Stand-by rank order: miners first, then traders, then suppliers (D-828 BL-T (a)). */
const STANDBY_RANK = ['miner', 'trader', 'supplier'] as const satisfies readonly RoleId[]

/**
 * BL-1a: ranks the stand-by candidates holding at node `stationId`: the first `n` (all roles together: miners first,
 * then traders, then suppliers, slot order within a role) show (1), the rest hide (2). Message rate; allocation-free.
 */
export function refreshStandby(s: ContactStore, stationId: string, n: number): void {
  let shown = 0
  for (let r = 0; r < STANDBY_RANK.length; r++) {
    const role = STANDBY_RANK[r]
    for (let i = 0; i < s.count; i++) {
      if (s.role[i] !== role || s.targetId[i] !== stationId || !isStandbyCandidate(s, i)) continue
      if (shown < n) {
        s.standby[i] = 1
        shown++
      } else {
        s.standby[i] = 2
      }
    }
  }
}

/**
 * BL-1a: re-ranks every station's stand-by candidates (after a batch of steps, spawns or removals) and clears the
 * mark of every other contact. Message rate; allocation-free (one ranking pass per station with candidates).
 */
export function refreshAllStandby(s: ContactStore, n: number): void {
  // 3 = a candidate not ranked yet this pass; its station's ranking writes 1 or 2 for every candidate there.
  for (let i = 0; i < s.count; i++) s.standby[i] = isStandbyCandidate(s, i) ? 3 : 0
  for (let i = 0; i < s.count; i++) if (s.standby[i] === 3) refreshStandby(s, s.targetId[i], n)
}

/** BL-2b (D-803 C4, D-828 BL-T (c)): the purpose label texts (`{ware}`, `{station}` filled by `applyJob`). */
export const PURPOSE_TEXT = {
  carry: '{ware} → {station}',
  load: 'Loading {ware} at {station}',
  standbyMiner: 'Standing by: ore market full',
  standbyHauler: 'Waiting for a cargo: buyers full',
} as const

/** BL-2b: resets the station display names to the manifest's `stations` (message rate). */
export function setStationNames(s: ContactStore, stations: readonly { id: string; name: string }[]): void {
  s.stationNames.clear()
  for (let i = 0; i < stations.length; i++) s.stationNames.set(stations[i].id, stations[i].name)
}

/**
 * BL-2b: sets slot's purpose label from the step's `job` (message rate; the only place the label string is built).
 * No job → '' (today's target text), so a step without one clears a stale "Ferrite → R" after the sale.
 */
export function applyJob(s: ContactStore, slot: number, job: Readonly<LocalJob> | undefined): void {
  if (job === undefined) {
    s.label[slot] = ''
    return
  }
  if (job.kind === 'standby') {
    s.label[slot] = s.role[slot] === 'miner' ? PURPOSE_TEXT.standbyMiner : PURPOSE_TEXT.standbyHauler
    return
  }
  const ware = job.ware === null ? '' : wareName(job.ware)
  const station = s.stationNames.get(job.to) ?? job.to
  s.label[slot] = (job.kind === 'carry' ? PURPOSE_TEXT.carry : PURPOSE_TEXT.load).replace('{ware}', ware).replace('{station}', station)
}
